/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './ad-unit-matching-label-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {SearchResource} from '../../../components/api/resources/resource/search';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-matching-label-targeting',
  template
})
export class AdUnitMatchingLabelTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.adUnitMatchingLabel;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputted: string[] = [];
  searchTerm = '';
  list: any = {searchResults: [], totalHits: 0, pageStart: 0, pageEnd: 0};
  listLoaded = false;
  searchMeta = {
    pageSize: 50,
    currentPage: 1,
    resolved: true
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summary = _.flatten(_.map(this.model || [], (item) => item.matchingLabels)).join(', ');
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.adUnitMatchingLabel,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    @Inject(SearchResource) private readonly searchResource: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
    this.tabController.register(ADN_TARGETING_TYPES.adUnitMatchingLabel.widget, () => {
      if (this.listLoaded) {
        return;
      }
      this.search(true);
    }, copyTemplate);
  }

  remove(value: string): void {
    _.remove(this.inputted, (input) => input === value);
  }

  search(newSearch?: boolean): void {
    this.searchMeta.resolved = false;
    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    this.searchResource.query({
      q: this.searchTerm || '',
      types: 'MatchingLabel',
      objectState: 'ACTIVE',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    }).then((page: any) => {
      this.ngZone.run(() => {
        this.list = page;
        this.listLoaded = true;
        this.searchMeta.resolved = true;
        this.cdr.markForCheck();
      });
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  isSelected(label: string): boolean {
    return this.inputted.indexOf(label) > -1;
  }

  toggleLabel(label: string): void {
    const idx = this.inputted.indexOf(label);
    if (idx > -1) {
      this.inputted.splice(idx, 1);
    } else {
      this.inputted.push(label);
    }
  }

  private initFromModel(): void {
    const inputted: string[] = [];
    _.forEach(this.model || [], (entryObj) => {
      inputted.push(entryObj.matchingLabels[0]);
    });
    this.inputted = inputted;
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.inputted, (v) => {
      this.model.push({matchingLabels: [v]});
    });
    this.initFromModel();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initFromModel();
  }
}
